import {
  FC,
  useCallback,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  FormEvent,
} from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { CloseModalButton, MainButton } from "components/Buttons";
import BasicModal from "./BasicModal";
import { ModalButton } from "components/Buttons";
import { PhoneInput, TextInput } from "components/Inputs";
import {
  ModalHeading,
  ModalContent,
  InfoDecorative,
  InputContainer,
  InnerContentForm,
  InnerContent,
  ConfirmBlock,
  StatusIconContainer,
} from "./styled";
import { AppText } from "components";
import { setupMFAVerification } from "api/auth";
import { update3dsPhoneNumber } from "api/cardProduct";
import { GlobalStore, UserStore, CardsStore } from "stores";
import { colors, restrictedFor3dsCountries } from "helpers/consts";
import countries from "helpers/countries";
import type { ModalStepsType } from "helpers/types";
import type {
  UsersCard,
  CardProductPhoneUpdateReq,
} from "api/types/cardProduct";

import { ReactComponent as LockIcon } from "assets/icons/lock-unlocked.svg";
import { ReactComponent as CheckIcon } from "assets/icons/check-circle.svg";

const initialPhonePref = "United Kingdom";

interface Change3dsPhoneModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: UsersCard | null;
  setProduct: Dispatch<SetStateAction<UsersCard | null>>;
}
const Change3dsPhoneModal: FC<Change3dsPhoneModalProps> = ({
  onClose,
  isOpen,
  product,
  setProduct,
}) => {
  const { t } = useTranslation();

  const { isMFAEnabled } = UserStore;
  const { cryptoCardsList, setCryptoCardsList } = CardsStore;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [twoFACode, setTwoFACode] = useState<string>("");
  const [userPhonePref, setUserPhonePref] = useState<string>(initialPhonePref);
  const [userPhone, setUserPhone] = useState<string>("");
  const [step, setStep] = useState<ModalStepsType>("main");

  useEffect(() => {
    const currentUserPhone = product?.card.phoneNumber;
    if (currentUserPhone) {
      const splitedNumber = currentUserPhone.split(" ");
      if (splitedNumber.length === 2) {
        const countryDialCode = countries.find(
          ({ dial_code }) => dial_code === splitedNumber[0]
        );
        if (countryDialCode) {
          setUserPhonePref(countryDialCode.name);
        }
        setUserPhone(splitedNumber[1]);
        return;
      }
      setUserPhone(splitedNumber[0].replace("+", "").replaceAll(" ", ""));
    }
  }, [product]);

  const change2Fa = useCallback(() => {
    setIsLoading(true);
    setupMFAVerification().finally(() => setIsLoading(false));
  }, [setIsLoading]);

  const updateCardData = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!product) {
        return;
      }

      const dialCode = countries.find(
        ({ name }) => name === userPhonePref
      )!.dial_code;

      const new3dsPhoneNumber = `${dialCode} ${userPhone.replaceAll(" ", "")}`;

      if (new3dsPhoneNumber === product.card.phoneNumber) {
        return;
      }

      setIsLoading(true);

      const updatePhoneData: CardProductPhoneUpdateReq = {
        phoneNumber: new3dsPhoneNumber,
        oneTimePassword: twoFACode,
      };

      update3dsPhoneNumber(product.card.id, updatePhoneData)
        .then(() => {
          const newProduct = { ...product };
          newProduct.card.phoneNumber = new3dsPhoneNumber;

          setProduct(newProduct);
          const updatedCards = (
            cryptoCardsList ? [...cryptoCardsList] : []
          ).map((card) =>
            card.card.id === product.card.id ? newProduct : card
          );
          setCryptoCardsList(updatedCards);
          setStep("success");
        })
        .catch((err) => {
          if (err?.response?.data?.message) {
            GlobalStore.setError(err.response.data.message);
          }
        })
        .finally(() => setIsLoading(false));
    },
    [
      product,
      setProduct,
      cryptoCardsList,
      setCryptoCardsList,
      twoFACode,
      userPhone,
      userPhonePref,
    ]
  );

  const resetState = useCallback(() => {
    setTwoFACode("");
    setUserPhone("");
    setStep("main");
    setUserPhonePref(initialPhonePref);
  }, []);

  return (
    <BasicModal
      isOpen={isOpen}
      onExited={resetState}
      onClose={onClose}
      showLoader={isLoading}
    >
      <ModalContent>
        <CloseModalButton onClose={onClose} label="close" />
        <ModalHeading $centered>{t("EDIT_PHONE_3DS")}</ModalHeading>

        {step === "main" && (
          <>
            {!isMFAEnabled ? (
              <InfoDecorative>
                <AppText fontSize={18} fontWeight={600}>
                  {t("TWO_FA_FOR_3DS_PHONE_EDIT")}
                </AppText>

                <MainButton
                  maxWidth="14rem"
                  style={{ marginTop: "1.6rem" }}
                  variant="contained"
                  onClick={() => change2Fa()}
                >
                  <LockIcon />
                  {t("ENABLE")}
                </MainButton>
              </InfoDecorative>
            ) : (
              <InnerContentForm onSubmit={(e) => updateCardData(e)}>
                <InputContainer $noMargin>
                  <AppText>{t("ENTER_PHONE_NUMBER_FOR_3DS_VERIFY")}</AppText>
                  <PhoneInput
                    name="new3dsPhoneNumber"
                    placeholder={t("ENTER_PHONE_NUMBER")}
                    value={userPhone}
                    setValue={(value) => setUserPhone(value)}
                    phonePref={userPhonePref}
                    setPref={(value) => setUserPhonePref(value)}
                  />
                  {userPhonePref &&
                    restrictedFor3dsCountries.includes(userPhonePref) && (
                      <AppText color={colors.primary_600} fontWeight={400}>
                        *{t("RESRTICTION_FOR_3DS")}
                      </AppText>
                    )}
                </InputContainer>

                <InputContainer $marginTop>
                  <AppText>{t("TWO_FA_FOR_PIN_CODE")}</AppText>
                  <TextInput
                    placeholder={t("CODE")}
                    value={twoFACode}
                    onChange={({ target: { value } }) => setTwoFACode(value)}
                    leftIcon={<LockIcon />}
                    autoComplete="off"
                    name="2fa-code"
                  />
                </InputContainer>

                <ModalButton
                  disabled={userPhone.length < 4 || twoFACode.length < 6}
                  type="submit"
                >
                  {t("SAVE")}
                </ModalButton>
              </InnerContentForm>
            )}
          </>
        )}
        {step === "success" && (
          <InnerContent>
            <ConfirmBlock>
              <StatusIconContainer $isStatic $isSuccess>
                <CheckIcon />
              </StatusIconContainer>

              <AppText fontSize={16} color={colors.gray_700}>
                {t("PHONE_3DS_SUCCESS_CHANGE")}
              </AppText>
            </ConfirmBlock>
            <ModalButton onClick={onClose}>{t("DONE")}</ModalButton>
          </InnerContent>
        )}
      </ModalContent>
    </BasicModal>
  );
};

export default observer(Change3dsPhoneModal);
